"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { categoryLabel } from "@/lib/analytics";
import { formatMoney } from "@/lib/format";
import { SelectedStartupTicker } from "@/components/dashboard/metrics-games/selected-startup-ticker";
import { computeOpportunityScore } from "@/components/dashboard/metrics-games/utils";
import { useTenKGame } from "@/components/dashboard/metrics-games/tenk-game-context";
import type { ScoreGameInputs } from "@/components/dashboard/metrics-games/types";
import type { StartupRecord } from "@/lib/types";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_LENGTH = 2;

type Slot = "search" | "selected" | "both";

type Props = {
  slot?: Slot;
  onLoadScoreInputs?: (inputs: ScoreGameInputs) => void;
  onLoadScore?: (score: number) => void;
};

export function OpportunityScoreStartupSelector({ slot = "both", onLoadScoreInputs, onLoadScore }: Props) {
  const ctx = useTenKGame();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<StartupRecord[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadLoading, setLoadLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchSearchResults = useCallback(async (query: string) => {
    if (query.length < SEARCH_MIN_LENGTH) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await fetch(
        `/api/startups?search=${encodeURIComponent(query)}&limit=20`,
        { cache: "no-store" }
      );
      if (!res.ok) {
        setSearchResults([]);
        return;
      }
      const json = await res.json();
      setSearchResults(json.data ?? []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchSearchResults(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchSearchResults]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleSelectStartup(slug: string) {
    if (!ctx) return;
    setLoadLoading(true);
    setShowDropdown(false);
    setSearchQuery("");
    setSearchResults([]);
    try {
      const res = await fetch(`/api/startups/${encodeURIComponent(slug)}/opportunity-score`, {
        cache: "no-store"
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.details ?? "Failed to load startup");
      }
      const json = await res.json();
      const inputs = json.inputs as ScoreGameInputs;
      const loader = onLoadScoreInputs ?? ctx.loadOpportunityScoreInputs;
      loader(inputs);
      onLoadScore?.(computeOpportunityScore(inputs));
      const startupRecord = json.startup as StartupRecord;
      ctx.setSelectedStartupForOpportunityScore(startupRecord);
    } catch (err) {
      console.error("Failed to load startup for opportunity score:", err);
    } finally {
      setLoadLoading(false);
    }
  }

  if (!ctx) return null;

  const showSearch = slot === "search" || slot === "both";
  const showSelected = slot === "selected" || slot === "both";

  async function handleLoadRandom() {
    if (!ctx) return;
    setLoadLoading(true);
    setShowDropdown(false);
    setSearchQuery("");
    setSearchResults([]);
    try {
      const res = await fetch("/api/market-ticker", { cache: "no-store" });
      if (!res.ok) throw new Error("Failed to load startups");
      const json = await res.json();
      const startups = (json.data ?? []) as StartupRecord[];
      if (startups.length === 0) throw new Error("No startups available");
      const random = startups[Math.floor(Math.random() * startups.length)];
      await handleSelectStartup(random.slug);
    } catch (err) {
      console.error("Failed to load random startup:", err);
    } finally {
      setLoadLoading(false);
    }
  }

  const searchBlock = showSearch && (
    <div className="tenk-selector tenk-selector--search" ref={dropdownRef}>
      <div className="tenk-selector-input-wrap">
        <input
          type="text"
          className="form-input tenk-selector-input"
          placeholder="Search your startup or load a random one…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
        />
        {searchLoading && <span className="tenk-selector-spinner" aria-hidden />}
      </div>
      {showDropdown && searchResults.length > 0 && (
        <ul className="tenk-selector-dropdown" role="listbox">
          {searchResults.map((s) => (
            <li
              key={s.slug}
              role="option"
              className="tenk-selector-option"
              onClick={() => handleSelectStartup(s.slug)}
            >
              <div className="ticker-startup">
                {s.icon && (
                  <img src={s.icon} alt="" className="ticker-logo" width={24} height={24} />
                )}
                <span className="ticker-name">{s.name}</span>
              </div>
              <span className="ticker-badge">{categoryLabel(s.category)}</span>
              <span className="tenk-selector-option-mrr">{formatMoney(s.mrr)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="tenk-selector-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={handleLoadRandom}
          disabled={loadLoading}
        >
          Load random
        </button>
      </div>
      {loadLoading && <p className="tenk-selector-loading">Loading startup data…</p>}
    </div>
  );

  const selectedBlock = showSelected && ctx.selectedStartupForOpportunityScore && (
    <div className="tenk-selector tenk-selector--centered">
      <SelectedStartupTicker
        slug={ctx.selectedStartupForOpportunityScore.slug}
        initialData={ctx.selectedStartupForOpportunityScore}
        onClear={() => ctx.setSelectedStartupForOpportunityScore(null)}
      />
    </div>
  );

  if (slot === "search") return searchBlock;
  if (slot === "selected") return selectedBlock;

  return (
    <div className="tenk-selector tenk-selector--centered" ref={dropdownRef}>
      <div className="tenk-selector-row">
        <div className="tenk-selector-input-wrap">
          <input
            type="text"
            className="form-input tenk-selector-input"
            placeholder="Search your startup or load a random one…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
          />
          {searchLoading && <span className="tenk-selector-spinner" aria-hidden />}
        </div>
        {ctx.selectedStartupForOpportunityScore && (
          <SelectedStartupTicker
            slug={ctx.selectedStartupForOpportunityScore.slug}
            initialData={ctx.selectedStartupForOpportunityScore}
            onClear={() => ctx.setSelectedStartupForOpportunityScore(null)}
          />
        )}
      </div>
      {showDropdown && searchResults.length > 0 && (
        <ul className="tenk-selector-dropdown" role="listbox">
          {searchResults.map((s) => (
            <li
              key={s.slug}
              role="option"
              className="tenk-selector-option"
              onClick={() => handleSelectStartup(s.slug)}
            >
              <div className="ticker-startup">
                {s.icon && (
                  <img src={s.icon} alt="" className="ticker-logo" width={24} height={24} />
                )}
                <span className="ticker-name">{s.name}</span>
              </div>
              <span className="ticker-badge">{categoryLabel(s.category)}</span>
              <span className="tenk-selector-option-mrr">{formatMoney(s.mrr)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="tenk-selector-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={handleLoadRandom}
          disabled={loadLoading}
        >
          Load random
        </button>
      </div>
      {loadLoading && <p className="tenk-selector-loading">Loading startup data…</p>}
    </div>
  );
}
